/**
 * カードタブの純粋な計算（DOM なし）。手札のまとめ方・通知の導出・使えない理由・倍率の表示・残り時間・弧の位置。
 * 画面の組み立ては index.ts。
 */
import type { ActiveEffect, GameState, RecentDuel, StateCard } from '../../api/types';
import type { Notice } from '../contract';
import { fmtRemaining } from '../../core/clock';

export type NeedKind = '' | 't' | 'a' | 'ta';

export interface CardInfo {
  id: string;
  name: string;
  /** 札の名前の折り返し位置（README ㊻a の札の表記） */
  label: [string, string?];
  /** README「札の説明」（state に説明が無いときの代わり） */
  desc: string;
  /** 選ぶもの：t=チーム a=エリア */
  need: NeedKind;
}

export const CATALOG: Record<string, CardInfo> = {
  queen: {
    id: 'queen',
    name: '女王の飛行',
    label: ['女王の', '飛行'],
    desc: '30分間、得点が2倍。最後にクリアしたスポットが知られ、相手にクリアされると効果を失う',
    need: '',
  },
  hornet: {
    id: 'hornet',
    name: 'スズメバチの襲来',
    label: ['スズメバチの', '襲来'],
    desc: '1チームが10分間、指定エリアでスポットを出せない',
    need: 'ta',
  },
  duel: {
    id: 'duel',
    name: '縄張り争い',
    label: ['縄張り', '争い'],
    desc: '相手を指定し、20分間でスポットを多くクリアしたチームが、相手から400点奪う',
    need: 't',
  },
  jelly: {
    id: 'jelly',
    name: 'ローヤルゼリー',
    label: ['ローヤル', 'ゼリー'],
    desc: '選んだエリアの倍率が1段上がる',
    need: 'a',
  },
  scout: {
    id: 'scout',
    name: '偵察蜂',
    label: ['偵察蜂'],
    desc: '全チームのおおよその位置が5分間見える',
    need: '',
  },
};

/** 偵察蜂の見える時間（他チームの偵察は終了時刻が state に無いので、開始から数える） */
export const SCOUT_MS = 300_000;

export function cardInfo(cardId: string): CardInfo | null {
  return CATALOG[cardId] ?? null;
}

// ---------------------------------------------------------------- 手札

export interface HandGroup {
  cardId: string;
  name: string;
  description: string;
  /** 未使用の team_card_id（古い順） */
  ids: number[];
  count: number;
}

/** 未使用の札を card_id ごとにまとめる（並びは最初に届いた順）。exclude は使った直後でまだ state に出ていない札 */
export function groupHand(cards: readonly StateCard[], exclude?: ReadonlySet<number>): HandGroup[] {
  const live = cards
    .filter((c) => c.used_at === null && !exclude?.has(c.team_card_id))
    .sort((a, b) => Date.parse(a.acquired_at) - Date.parse(b.acquired_at) || a.team_card_id - b.team_card_id);
  const out: HandGroup[] = [];
  for (const c of live) {
    let g = out.find((x) => x.cardId === c.card_id);
    if (!g) {
      g = {
        cardId: c.card_id,
        name: c.name || cardInfo(c.card_id)?.name || c.card_id,
        description: c.description || cardInfo(c.card_id)?.desc || '',
        ids: [],
        count: 0,
      };
      out.push(g);
    }
    g.ids.push(c.team_card_id);
    g.count += 1;
  }
  return out;
}

/** 弧に並べる札の位置（README ㊺/㊼：幅80px、少し重ね、両端ほど下げて傾ける） */
export interface ArcSlot {
  left: number;
  rot: number;
  ty: number;
}
export function arcSlots(n: number, width: number, cardW = 80): ArcSlot[] {
  const step = n > 1 ? Math.min(cardW + 2, (width - 64 - cardW) / (n - 1)) : 0;
  const x0 = (width - (step * (n - 1) + cardW)) / 2;
  return Array.from({ length: n }, (_, i) => {
    const k = i - (n - 1) / 2;
    return { left: x0 + i * step, rot: k * (n > 4 ? 5 : 6), ty: Math.abs(k) * Math.abs(k) * (n > 4 ? 2.2 : 4) };
  });
}

// ---------------------------------------------------------------- 残り時間・倍率

/** 「次の札まで」の m:ss。次の配布が無い（NULL）ときは null */
export function countdownText(nextIso: string | null | undefined, nowMs: number): string | null {
  if (!nextIso) return null;
  const t = Date.parse(nextIso);
  if (Number.isNaN(t)) return null;
  return fmtRemaining(Math.max(0, t - nowMs));
}

/** 倍率の表記。小数1桁以上で、末尾の余分な0は落とす（1.40→1.4、2→2.0、1.25→1.25） */
export function fmtMult(x: number): string {
  let s = x.toFixed(2);
  if (s.endsWith('0')) s = s.slice(0, -1);
  return s;
}

export interface JellyText {
  from: string;
  to: string;
  atMax: boolean;
  /** 「×1.4 → ×1.6」／上限は「×2.0 上限」 */
  text: string;
}

/** ローヤルゼリー：エリアの倍率 ×a → ×b。上限（clear_max_multiplier）なら押せない */
export function jellyText(mult: number, step: number, max: number | null | undefined): JellyText {
  const atMax = max != null && mult >= max - 1e-9;
  const next = max != null ? Math.min(max, mult + step) : mult + step;
  const from = fmtMult(mult);
  const to = fmtMult(next);
  return { from, to, atMax, text: atMax ? `×${from} 上限` : `×${from} → ×${to}` };
}

// ---------------------------------------------------------------- 使えない理由（52）

export type BlockCode = 'duel_busy' | 'card_busy_in_server';

/** 押す前に分かる理由。使用中の欄（自分の行も受けた行も）にある効果で判定する */
export function preDisableReason(cardId: string, state: GameState, nowMs: number): BlockCode | null {
  const me = state.team.team_id;
  const live = (e: ActiveEffect): boolean => e.ends_at === null || Date.parse(e.ends_at) > nowMs;
  if (cardId === 'duel') {
    const busy = state.active_effects.some(
      (e) => e.effect_type === 'duel' && live(e) && (e.source_team === me || e.target_team === me),
    );
    return busy ? 'duel_busy' : null;
  }
  if (cardId === 'queen') {
    const busy = state.active_effects.some((e) => e.card_id === 'queen' && live(e));
    return busy ? 'card_busy_in_server' : null;
  }
  return null;
}

/** ボタンに出す理由（句点なし） */
export function errorText(code: string): string {
  switch (code) {
    case 'duel_busy':
      return '縄張り争いが終わるまで使えません';
    case 'card_busy_in_server':
      return '女王の飛行が終わるまで使えません';
    case 'scout_no_location':
      return '相手の位置が取れませんでした';
    case 'network':
      return '通信できませんでした';
    case 'card_not_owned':
    case 'card_already_used':
      return 'この札はもう使えません';
    case 'invalid_target_team':
      return 'その相手は選べません';
    case 'area_required':
      return 'エリアを選んでください';
    case 'event_not_running':
    case 'event_ended':
      return 'いまは使えません';
    case 'card_not_usable':
      return 'この札は使えません';
    default:
      return '使えませんでした';
  }
}

// ---------------------------------------------------------------- チームの選択肢（48）

export interface TeamOption {
  id: string;
  name: string;
  rank: number | null;
  total: number | null;
  inDuel: boolean;
}

export function teamOptions(state: GameState): TeamOption[] {
  const me = state.team.team_id;
  const rank = new Map<string, { rank: number; total: number }>();
  state.ranking?.forEach((r, i) => rank.set(r.team_id, { rank: i + 1, total: r.total }));
  return state.server_teams
    .filter((t) => t.team_id !== me)
    .map((t) => ({
      id: t.team_id,
      name: t.team_name,
      rank: rank.get(t.team_id)?.rank ?? null,
      total: rank.get(t.team_id)?.total ?? null,
      inDuel: t.in_duel,
    }));
}

/** その札で、このチームを選べるか（縄張り争いの最中のチームは縄張り争いに指名できない） */
export function teamPickable(cardId: string, t: TeamOption): boolean {
  return !(cardId === 'duel' && t.inDuel);
}

// ---------------------------------------------------------------- 使用中の効果（50・51）

export interface DuelScore {
  who: string;
  me: number;
  op: number;
}

export interface EffectRow {
  key: string;
  cardId: string;
  name: string;
  /** 受けている効果（赤い行） */
  received: boolean;
  /** 中身（duel があるときは数の表示に使う） */
  text: string;
  duel?: DuelScore;
  /** 偵察蜂の「地図で見る」 */
  mapLink?: boolean;
  startMs: number;
  endMs: number;
  /** 縄張り争いが時間切れで、結果を待っている */
  waiting?: boolean;
  /** 縄張り争いの結果（3秒だけ出す） */
  res?: string;
}

function areaName(state: GameState, areaId: string | null): string {
  if (!areaId) return '';
  return state.areas.find((a) => a.area_id === areaId)?.name ?? areaId;
}

/** 使用中の欄の行。使った順（開始が早い順）。ローヤルゼリーは出さない。縄張り争いは時間切れでも結果が来るまで残す */
export function deriveRows(state: GameState, nowMs: number): EffectRow[] {
  const me = state.team.team_id;
  const rows: EffectRow[] = [];
  for (const e of state.active_effects) {
    const startMs = Date.parse(e.starts_at);
    const endMs = e.ends_at ? Date.parse(e.ends_at) : Number.POSITIVE_INFINITY;
    const key = `e${e.effect_id}`;
    const over = endMs <= nowMs;
    if (e.effect_type === 'multiplier_self' && e.card_id === 'queen') {
      if (over) continue;
      const mine = e.source_team === me;
      let text: string;
      if (mine) text = `得点${+(e.multiplier ?? 2)}倍`;
      else {
        const spot = lastSpotName(state, e.effect_id);
        text = [e.source_team_name ?? '', spot].filter(Boolean).join(' ');
      }
      rows.push({ key, cardId: 'queen', name: CATALOG.queen!.name, received: !mine, text, startMs, endMs });
    } else if (e.effect_type === 'block_area') {
      if (over) continue;
      if (e.source_team === me) {
        const text = [e.target_team_name ?? '', areaName(state, e.area_id)].filter(Boolean).join(' ');
        rows.push({ key, cardId: 'hornet', name: CATALOG.hornet!.name, received: false, text, startMs, endMs });
      } else if (e.targets_me) {
        rows.push({
          key,
          cardId: 'hornet',
          name: CATALOG.hornet!.name,
          received: true,
          text: `${areaName(state, e.area_id)}で出せない`,
          startMs,
          endMs,
        });
      }
    } else if (e.effect_type === 'duel') {
      const mine = e.source_team === me;
      if (!mine && e.target_team !== me) continue;
      const sc = e.duel?.source_count ?? 0;
      const tc = e.duel?.target_count ?? 0;
      const duel: DuelScore = mine
        ? { who: e.target_team_name ?? '', me: sc, op: tc }
        : { who: e.source_team_name ?? '', me: tc, op: sc };
      rows.push({
        key,
        cardId: 'duel',
        name: CATALOG.duel!.name,
        received: !mine,
        text: `${duel.who}と ${duel.me} 対 ${duel.op}`,
        duel,
        startMs,
        endMs,
        waiting: over,
      });
    }
  }
  for (const s of state.scouts) {
    const startMs = Date.parse(s.starts_at);
    const endMs = s.ends_at ? Date.parse(s.ends_at) : startMs + SCOUT_MS;
    if (endMs <= nowMs) continue;
    rows.push({
      key: `e${s.effect_id}`,
      cardId: 'scout',
      name: CATALOG.scout!.name,
      received: false,
      text: '地図で見る',
      mapLink: true,
      startMs,
      endMs,
    });
  }
  const mineScout = new Set(state.scouts.map((s) => s.effect_id));
  for (const s of state.recent_scouts) {
    if (s.source_team === me || mineScout.has(s.effect_id)) continue;
    const startMs = Date.parse(s.starts_at);
    const endMs = startMs + SCOUT_MS;
    if (endMs <= nowMs) continue;
    rows.push({
      key: `e${s.effect_id}`,
      cardId: 'scout',
      name: CATALOG.scout!.name,
      received: true,
      text: `${s.source_team_name}に見られている`,
      startMs,
      endMs,
    });
  }
  return rows.sort((a, b) => a.startMs - b.startMs || a.key.localeCompare(b.key));
}

function lastSpotName(state: GameState, effectId: number): string {
  const names = state.revealed.filter((r) => r.effect_id === effectId && r.spot_name).map((r) => r.spot_name as string);
  return names[names.length - 1] ?? '';
}

export interface DuelOutcome {
  won: boolean;
  text: string;
}

/** 縄張り争いの結果（自分が関わったときだけ）。同数は指名された側の勝ち（サーバー015） */
export function duelOutcome(d: RecentDuel, me: string): DuelOutcome | null {
  if (d.source_team !== me && d.target_team !== me) return null;
  const sourceWins = d.source_count > d.target_count;
  const won = d.source_team === me ? sourceWins : !sourceWins;
  return { won, text: won ? `勝ち ＋${d.duel_points}` : `負け −${d.duel_points}` };
}

/** 手に入った縄張り争いの結果を出すべきものを返す（出し済みと、自分の行が無かったものは除く） */
export function finishedDuels(
  next: GameState,
  prev: GameState | null,
  shown: ReadonlySet<number>,
  hadRow: (effectId: number) => boolean,
): { effectId: number; outcome: DuelOutcome }[] {
  const out: { effectId: number; outcome: DuelOutcome }[] = [];
  const me = next.team.team_id;
  for (const d of next.recent_duels) {
    if (shown.has(d.effect_id)) continue;
    const outcome = duelOutcome(d, me);
    if (!outcome) continue;
    const wasActive = prev?.active_effects.some((e) => e.effect_id === d.effect_id) ?? false;
    if (!wasActive && !hadRow(d.effect_id)) continue;
    out.push({ effectId: d.effect_id, outcome });
  }
  return out;
}

// ---------------------------------------------------------------- 通知（51）

/**
 * 隠れているタブでも出す通知を、前の状態との差から作る。seen は effect_id のキー集合（呼び出し側が持ち続ける）で、
 * 同じ効果は何度の取得でも1回だけ。最初の取得（prev が null）は通知せず seen に入れるだけ。
 * ローヤルゼリーは知らせない。自分が使った札も知らせない。
 */
export function deriveNotices(prev: GameState | null, next: GameState, seen: Set<string>): Notice[] {
  const me = next.team.team_id;
  const out: Notice[] = [];
  const prevIds = new Set(prev?.active_effects.map((e) => `e${e.effect_id}`) ?? []);
  for (const s of prev?.recent_scouts ?? []) prevIds.add(`e${s.effect_id}`);
  const fresh = (key: string): boolean => {
    const known = seen.has(key);
    seen.add(key);
    return !known && prev !== null && !prevIds.has(key);
  };
  for (const e of next.active_effects) {
    const key = `e${e.effect_id}`;
    if (!fresh(key)) continue;
    if (e.source_team === me) continue;
    const src = e.source_team_name ?? '';
    if (e.effect_type === 'block_area') {
      if (e.targets_me) {
        out.push({
          heading: `${CATALOG.hornet!.name}・${src}`,
          body: `${areaName(next, e.area_id)}を襲われました`,
          at: e.starts_at,
        });
      } else {
        out.push({
          heading: CATALOG.hornet!.name,
          body: `${src}が${e.target_team_name ?? ''}を襲いました`,
          at: e.starts_at,
        });
      }
    } else if (e.effect_type === 'duel') {
      if (e.target_team === me) {
        out.push({ heading: `${CATALOG.duel!.name}・${src}`, body: '縄張り争いを仕掛けられました', at: e.starts_at });
      } else {
        out.push({ heading: CATALOG.duel!.name, body: `${src}が${e.target_team_name ?? ''}に縄張り争いを仕掛けました`, at: e.starts_at });
      }
    } else if (e.effect_type === 'multiplier_self' && e.card_id === 'queen') {
      const spot = lastSpotName(next, e.effect_id);
      out.push({
        heading: `${CATALOG.queen!.name}・${src}`,
        body: spot ? `${spot}をクリアすると止められます` : 'スポットをクリアすると止められます',
        at: e.starts_at,
      });
    }
  }
  for (const s of next.recent_scouts) {
    const key = `e${s.effect_id}`;
    if (!fresh(key)) continue;
    if (s.source_team === me) continue;
    out.push({
      heading: `${CATALOG.scout!.name}・${s.source_team_name}`,
      body: '全チームの位置が5分間見られます',
      at: s.starts_at,
    });
  }
  // 他チーム同士の縄張り争いの決着
  const prevDuels = new Set(prev?.recent_duels.map((d) => d.effect_id) ?? []);
  for (const d of next.recent_duels) {
    const key = `d${d.effect_id}`;
    const known = seen.has(key);
    seen.add(key);
    if (known || prev === null || prevDuels.has(d.effect_id)) continue;
    if (d.source_team === me || d.target_team === me) continue;
    const winner = d.source_count > d.target_count ? d.source_team_name : d.target_team_name;
    out.push({ heading: CATALOG.duel!.name, body: `${winner}が縄張り争いに勝ちました`, at: d.resolved_at });
  }
  return out;
}
